import { useState, useEffect } from 'react';
import { Image, Skeleton, Box } from '@mantine/core';
import { IconBook } from '@tabler/icons-react';
import type { ImageProps, BoxProps } from '@mantine/core';

type RecipeImageProps = {
  src: string | undefined | null;
  alt: string;
  height?: BoxProps['h'];
  width?: BoxProps['w'];
  w?: BoxProps['w'];
  h?: BoxProps['h'];
  fit?: ImageProps['fit'];
  radius?: BoxProps['style'] extends React.CSSProperties ? string : string;
  draggable?: boolean;
  style?: React.CSSProperties;
  fallbackIconSize?: number;
  fadeDuration?: number;
  fallback?: React.ReactNode;
  crossfade?: boolean; // Gentle crossfade between src changes
};

export const RecipeImage = ({
  src,
  alt,
  height,
  width,
  w,
  h,
  fit = 'cover',
  radius,
  draggable,
  style,
  fallbackIconSize = 48,
  fadeDuration = 300,
  fallback,
  crossfade = false,
}: RecipeImageProps) => {
  const [loaded, setLoaded] = useState(false);
  const [errored, setErrored] = useState(false);
  // True when the image was already decoded as its node attached (remount
  // with the file in browser cache — e.g. back navigation). Suppresses the
  // fade-in so content the user just saw doesn't dim and re-appear.
  const [instant, setInstant] = useState(false);
  const [prevSrc, setPrevSrc] = useState(src);
  // Crossfade: old image rendered as an overlay that fades OUT,
  // revealing the new image underneath. This avoids the cached-image
  // race where onLoad fires before the browser paints opacity:0.
  const [crossfadeSrc, setCrossfadeSrc] = useState<string | null>(null);
  const [crossfadeFading, setCrossfadeFading] = useState(false);

  // Reset state synchronously during render when src changes.
  // Using useEffect for this causes a race: cached images fire onLoad
  // during commit (before effects run), then the effect resets loaded=false,
  // leaving the image permanently invisible.
  if (prevSrc !== src) {
    if (crossfade && prevSrc) {
      if (loaded && !crossfadeSrc) {
        // Current image is visible — use it as the fade-out overlay
        setCrossfadeSrc(prevSrc);
        setCrossfadeFading(false);
      }
      // If there's already an overlay mid-fade (rapid swipes), keep it —
      // the original image continues fading out while the latest src loads
    }
    setPrevSrc(src);
    setLoaded(false);
    setErrored(false);
    setInstant(false);
  }

  // A cached image can be complete before onLoad is delivered (load events
  // arrive in a later task, after the browser has already painted a frame),
  // so a remount would flash the skeleton and fade the image back in. Ref
  // callbacks run during commit — marking the image loaded here re-renders
  // it visible before that first paint.
  const handleImageRef = (node: HTMLImageElement | null) => {
    if (node && node.complete && node.naturalWidth > 0 && !loaded && !errored) {
      setLoaded(true);
      setInstant(true);
    }
  };

  // After the overlay paints at opacity 1, trigger the fade-out transition
  useEffect(() => {
    if (crossfadeSrc && !crossfadeFading) {
      requestAnimationFrame(() => {
        setCrossfadeFading(true);
      });
    }
  }, [crossfadeSrc, crossfadeFading]);

  // Safety timeout: clear overlay if onTransitionEnd doesn't fire
  useEffect(() => {
    if (crossfadeSrc && crossfadeFading) {
      const timer = setTimeout(() => setCrossfadeSrc(null), fadeDuration + 100);
      return () => clearTimeout(timer);
    }
  }, [crossfadeSrc, crossfadeFading, fadeDuration]);

  const finalW = w ?? width;
  const finalH = h ?? height;

  const radiusStyle = radius
    ? `var(--mantine-radius-${radius})`
    : undefined;

  // No src — show fallback immediately (no skeleton needed)
  if (!src) {
    return (
      <Box
        w={finalW}
        h={finalH}
        style={{
          backgroundColor: 'var(--mantine-color-gray-1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: radiusStyle,
          overflow: 'hidden',
        }}
      >
        {fallback ?? (
          <IconBook size={fallbackIconSize} color="var(--mantine-color-gray-5)" />
        )}
      </Box>
    );
  }

  return (
    <Box
      w={finalW}
      h={finalH}
      style={{
        position: 'relative',
        overflow: 'hidden',
        borderRadius: radiusStyle,
      }}
    >
      {/* Skeleton visible until image loads or errors (hidden during crossfade) */}
      {!loaded && !errored && !crossfadeSrc && (
        <Skeleton
          width="100%"
          height="100%"
          radius={0}
          style={{ position: 'absolute', inset: 0 }}
        />
      )}

      {/* Error fallback overlay */}
      {errored && (
        <Box
          style={{
            position: 'absolute',
            inset: 0,
            backgroundColor: 'var(--mantine-color-gray-1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {fallback ?? (
            <IconBook size={fallbackIconSize} color="var(--mantine-color-gray-5)" />
          )}
        </Box>
      )}

      {/* Image — fades in on load (immediately visible when already cached) */}
      <Image
        ref={handleImageRef}
        src={src}
        alt={alt}
        width="100%"
        height="100%"
        fit={fit}
        radius={0}
        draggable={draggable}
        onLoad={() => setLoaded(true)}
        onError={() => {
          setLoaded(true);
          setErrored(true);
        }}
        style={{
          ...style,
          opacity: loaded && !errored ? 1 : 0,
          transition: instant ? 'none' : `opacity ${fadeDuration}ms ease-in-out`,
        }}
      />

      {/* Crossfade overlay: old image fades OUT to reveal the new one underneath */}
      {crossfadeSrc && (
        <Image
          src={crossfadeSrc}
          alt=""
          width="100%"
          height="100%"
          fit={fit}
          radius={0}
          onTransitionEnd={() => setCrossfadeSrc(null)}
          style={{
            ...style,
            position: 'absolute',
            inset: 0,
            opacity: crossfadeFading ? 0 : 1,
            transition: `opacity ${fadeDuration}ms ease-in-out`,
          }}
        />
      )}
    </Box>
  );
};
